import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges, ViewChild, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DocumentUploadComponent } from '../../../shared/components/document-upload';
import type { ExtendedDocumentFile } from '../../../core/models/upload.models';
import { DocumentUploadService } from '../../../core/services/document-upload.service';
import { DocumentFile, DocumentValidationConfig, DocumentFileItem } from '../../../core/models/upload.models';
import { LeadsService } from '../../../core/services/leads.service';
import { DocumentUploadResponse, DocumentDeleteResponse, DocumentListResponse, DocumentDownloadResponse, CreateFolderResponse, CreateFolderRequest, GenericApiResponse } from '../../../core/models/upload.models';
import { forkJoin, Observable, of, catchError, map, Subscription, share } from 'rxjs';
import { ActionResult } from '../../../core/models/action-result-toolbar.models';
import { ActionFeedbackService } from '../../../core/services/action-feedback.service';
import { presentActionResultWithActionFeedback } from '../../../shared/utils/action-result-feedback.util';
import {
  extractUploadHttpErrorMessage,
  primaryApiValidationMessage,
} from '../../../shared/utils/upload-error-message.util';
import { FilePreviewModalComponent } from '../../../shared/components/file-preview-modal/file-preview-modal.component';
import {
  pickAttachmentDisplayUrlFromApiData,
  pickAttachmentDisplayUrlFromDocument
} from '../../../shared/utils/attachment-display-url.util';

@Component({
  selector: 'app-leads-documents-tab',
  standalone: true,
  imports: [CommonModule, DocumentUploadComponent, FilePreviewModalComponent],
  templateUrl: './leads-documents-tab.component.html',
  styleUrl: './leads-documents-tab.component.scss',
  host: {
    'style': 'display: flex; width: 100%; height: 100%;'
  }
})
export class LeadsDocumentsTab implements OnInit, OnDestroy, OnChanges {
  @Input() entityId?: number | null;
  @Input() disabled: boolean = false;
  @Input() entityRootFolder?: string | null;
  @Input() searchTerm: string = '';
  @Output() documentCountChange = new EventEmitter<number>();
  @ViewChild(DocumentUploadComponent) documentUploadComponent!: DocumentUploadComponent;

  private readonly actionFeedback = inject(ActionFeedbackService);

  documents: ExtendedDocumentFile[] = [];
  loading = false;
  error: string | null = null;

  // Folder hierarchy tracking
  folderHierarchyId: string = 'ROOT';
  // Navigation stack to track folder history for back button
  // Stores folder info: { folderId, folderName, folderPath, folderNodeId }
  private folderNavigationStack: Array<{
    folderId: string;
    folderName: string;
    folderPath: string;
    folderNodeId: number | null;
    documents: ExtendedDocumentFile[];
  }> = [];
  // Current folder's node ID for uploads
  currentFolderNodeId: number | null = null;

  // Multi-column folder navigation for list view
  folderLevels: Array<{
    folderId: string;
    folderName: string;
    folderPath: string;
    documents: ExtendedDocumentFile[];
    folderNodeId: number | null;
    loading: boolean;
    error: string | null;
  }> = [];
  currentViewMode: 'gallery' | 'list' = 'gallery';

  // Upload cancellation tracking
  private uploadSubscriptions = new Map<string, Subscription>();
  private uploadIntervals = new Map<string, number>();

  /**
   * Get the base folder ID - use entityRootFolder if available, otherwise 'ROOT'
   */
  public getBaseFolderId(): string {
    return this.entityRootFolder || 'ROOT';
  }

  /**
   * Extract the first segment from a path (first slash, first number, second slash)
   * Examples: "/1/2/3/" -> "/1/", "/12/2/3/" -> "/12/"
   */
  private extractFirstPathSegment(path: string): string {
    if (!path || typeof path !== 'string') {
      return path;
    }
    // Match pattern: /<number>/ at the start of the string
    const match = path.match(/^(\/\d+\/)/);
    return match ? match[1] : path;
  }

  /**
   * Get the active folder node ID for uploads based on current view mode
   * In gallery view: uses current folder's node ID
   * In list view: uses the rightmost folder level's node ID
   */
  public getActiveFolderNodeId(): number | null {
    if (this.currentViewMode === 'list') {
      // In list view: use the rightmost folder level's node ID
      if (this.folderLevels && this.folderLevels.length > 0) {
        const rightmostLevel = this.folderLevels[this.folderLevels.length - 1];
        return rightmostLevel.folderNodeId;
      }
      // Fallback to current folder node ID if no folder levels
      return this.currentFolderNodeId;
    } else {
      // In gallery view: use current folder's node ID
      return this.currentFolderNodeId;
    }
  }

  /**
   * Reload the active folder based on current view mode
   * In gallery view: reloads the current folder
   * In list view: reloads the rightmost folder level and updates parent folder itemCount
   */
  private reloadActiveFolder(): void {
    if (this.currentViewMode === 'list') {
      // In list view: reload the rightmost folder level
      if (this.folderLevels && this.folderLevels.length > 0) {
        // Check if the rightmost level matches the current folderHierarchyId
        const rightmostLevel = this.folderLevels[this.folderLevels.length - 1];
        if (rightmostLevel.folderId === this.folderHierarchyId) {
          // Reload the existing level
          this.reloadFolderLevel(rightmostLevel);
        } else {
          // Folder hierarchy changed (e.g., transition from base to new folder), reload main documents
          // This will properly initialize folderLevels with the new folderHierarchyId
          this.loadDocuments();
        }
        // Also reload parent folder to update its itemCount
        this.updateParentFolderItemCount();
      } else {
        // Fallback: reload main documents (this will initialize folderLevels)
        this.loadDocuments();
      }
    } else {
      // In gallery view: reload the current folder
      this.loadDocuments();
      // Also update parent folder's itemCount if we're inside a folder
      if (this.folderNavigationStack.length > 0) {
        this.updateParentFolderItemCountInGalleryView();
      }
    }
  }

  /**
   * Reload the active folder with suppressed loading state to prevent UI flashing after uploads
   */
  private reloadActiveFolderWithSuppressedLoading(): void {
    if (this.currentViewMode === 'list') {
      // In list view: reload the rightmost folder level
      if (this.folderLevels && this.folderLevels.length > 0) {
        // Check if the rightmost level matches the current folderHierarchyId
        const rightmostLevel = this.folderLevels[this.folderLevels.length - 1];
        if (rightmostLevel.folderId === this.folderHierarchyId) {
          // Reload the existing level (note: reloadFolderLevel doesn't have suppress option, but it's fine)
          this.reloadFolderLevel(rightmostLevel);
        } else {
          // Folder hierarchy changed, reload main documents with suppressed loading
          this.loadDocuments(undefined, true);
        }
        // Also reload parent folder to update its itemCount
        this.updateParentFolderItemCount();
      } else {
        // Fallback: reload main documents with suppressed loading
        this.loadDocuments(undefined, true);
      }
    } else {
      // In gallery view: reload the current folder with suppressed loading
      this.loadDocuments(undefined, true);
      // Also update parent folder's itemCount if we're inside a folder
      if (this.folderNavigationStack.length > 0) {
        this.updateParentFolderItemCountInGalleryView();
      }
    }
  }

  /**
   * Reload a specific folder level
   */
  private reloadFolderLevel(level: {
    folderId: string;
    folderName: string;
    folderPath: string;
    documents: ExtendedDocumentFile[];
    folderNodeId: number | null;
    loading: boolean;
    error: string | null;
  }): void {
    if (!this.entityId) {
      return;
    }

    level.loading = true;
    level.error = null;

    this.leadsService.getLeadDocuments(
      this.entityId,
      level.folderPath
    ).subscribe({
      next: (response: DocumentListResponse) => {
        level.loading = false;
        
        if (response.success && response.data) {
          const responseData = response.data as any;
          let rows = responseData?.rows || (Array.isArray(responseData) ? responseData : []);
          
          // Extract folder node ID from first item
          if (rows.length > 0) {
            const firstItem = rows[0];
            level.folderNodeId = firstItem?.id ? Number(firstItem.id) : null;
            rows = rows.slice(1);
          }
          
          // Transform and store documents
          level.documents = this.documentUploadService.transformApiDocuments(rows);
        } else {
          level.error = response.message || 'Failed to load documents';
        }
      },
      error: (error) => {
        level.loading = false;
        level.error = error.message || 'Failed to load documents';
        console.error('Error reloading folder level:', error);
      }
    });
  }

  /**
   * Update parent folder's itemCount in list view
   * This is called after files are uploaded to refresh the current folder's item count in the parent column
   */
  private updateParentFolderItemCount(): void {
    if (!this.entityId || !this.folderLevels || this.folderLevels.length < 2) {
      // Need at least 2 levels to have a parent folder
      return;
    }

    // Get the current folder (rightmost column) and parent level (previous column)
    const currentLevelIndex = this.folderLevels.length - 1;
    const currentLevel = this.folderLevels[currentLevelIndex];
    const parentLevel = this.folderLevels[currentLevelIndex - 1];
    
    if (!currentLevel || !parentLevel || !currentLevel.folderPath) {
      return;
    }

    // Reload parent folder to get updated itemCount for the current folder from API
    this.leadsService.getLeadDocuments(
      this.entityId,
      parentLevel.folderPath
    ).subscribe({
      next: (response: DocumentListResponse) => {
        if (response.success && response.data) {
          const responseData = response.data as any;
          let rows = responseData?.rows || (Array.isArray(responseData) ? responseData : []);
          
          // Skip the first item (parent folder itself) and find the current folder in the list
          if (rows.length > 1) {
            rows = rows.slice(1);
            
            // Find the current folder in the parent folder's documents
            const currentFolderInParent = rows.find((item: any) => 
              item.kind === 'Folder' && item.folderPath === currentLevel.folderPath
            );
            
            if (currentFolderInParent) {
              const updatedItemCount = currentFolderInParent.itemCount !== undefined 
                ? Number(currentFolderInParent.itemCount) 
                : undefined;
              
              // Update the current folder's itemCount in the parent level's documents
              const parentFolder = parentLevel.documents.find(doc => 
                doc.isFolder && doc.folderPath === currentLevel.folderPath
              );
              
              if (parentFolder && updatedItemCount !== undefined) {
                parentFolder.itemCount = updatedItemCount;
              }
            }
          }
        }
      },
      error: (error) => {
        console.error('Error updating parent folder itemCount:', error);
      }
    });
  }

  /**
   * Update parent folder's itemCount in gallery view
   * This is called after files are uploaded to refresh the current folder's item count in the navigation stack
   */
  private updateParentFolderItemCountInGalleryView(): void {
    if (!this.entityId || this.folderNavigationStack.length === 0) {
      // Need at least one folder in the navigation stack to have a parent folder
      return;
    }

    // Get the parent folder (last item in navigation stack)
    const parentFolderInfo = this.folderNavigationStack[this.folderNavigationStack.length - 1];
    
    if (!parentFolderInfo || !parentFolderInfo.folderPath) {
      return;
    }

    // Reload parent folder to get updated itemCount for the current folder from API
    this.leadsService.getLeadDocuments(
      this.entityId,
      parentFolderInfo.folderPath
    ).subscribe({
      next: (response: DocumentListResponse) => {
        if (response.success && response.data) {
          const responseData = response.data as any;
          let rows = responseData?.rows || (Array.isArray(responseData) ? responseData : []);
          
          // Skip the first item (parent folder itself) and find the current folder in the list
          if (rows.length > 1) {
            rows = rows.slice(1);
            
            // Find the current folder in the parent folder's documents
            const currentFolderInParent = rows.find((item: any) => 
              item.kind === 'Folder' && item.folderPath === this.folderHierarchyId
            );
            
            if (currentFolderInParent) {
              const updatedItemCount = currentFolderInParent.itemCount !== undefined 
                ? Number(currentFolderInParent.itemCount) 
                : undefined;
              
              // Update the current folder's itemCount in the parent folder's documents in the navigation stack
              const parentFolder = parentFolderInfo.documents.find(doc => 
                doc.isFolder && doc.folderPath === this.folderHierarchyId
              );
              
              if (parentFolder && updatedItemCount !== undefined) {
                parentFolder.itemCount = updatedItemCount;
              }
            }
          }
        }
      },
      error: (error) => {
        console.error('Error updating parent folder itemCount in gallery view:', error);
      }
    });
  }

  /**
   * Check if we're at the base folder level
   */
  private isAtBaseFolder(): boolean {
    const baseFolder = this.getBaseFolderId();
    return this.folderHierarchyId === baseFolder;
  }

  // File preview properties
  showPreviewModal = false;
  previewDocument: ExtendedDocumentFile | null = null;

  // Upload tracking properties
  private uploadResults: { success: number; failed: number } = { success: 0, failed: 0 };
  // Track the folder path from the first successful upload when at base folder
  private firstUploadPath: string | null = null;
  // Flag to track if we just uploaded files and should retry if empty
  private justUploadedFiles: boolean = false;
  private retryCount: number = 0;
  private readonly maxRetries: number = 5;

  /**
   * Show upload result via toast
   */
  private showUploadResult(action: string, count: number, itemLabel: string, actionPastTense: string, _autoClose: boolean = true): void {
    const result: ActionResult = {
      action,
      count,
      itemLabel,
      actionPastTense,
      timestamp: new Date(),
      isCustomMessage: false
    };
    presentActionResultWithActionFeedback(this.actionFeedback, result, { allowWrap: true });
  }

  /**
   * Show batch upload result summary
   */
  private showBatchUploadResult(): void {
    const totalUploads = this.uploadResults.success + this.uploadResults.failed;

    if (totalUploads === 0) return;

    if (this.uploadResults.failed === 0) {
      const count = this.uploadResults.success;
      this.showUploadResult(
        'uploaded',
        count,
        `file${count > 1 ? 's' : ''}`,
        'uploaded successfully',
        true
      );
      return;
    }

    if (this.uploadResults.success === 0) {
      const count = this.uploadResults.failed;
      const result: ActionResult = {
        action: 'error',
        count,
        itemLabel: `file${count > 1 ? 's' : ''}`,
        actionPastTense: 'failed to upload',
        timestamp: new Date(),
        isCustomMessage: false,
        isError: true
      };
      presentActionResultWithActionFeedback(this.actionFeedback, result, { allowWrap: true });
      return;
    }

    this.actionFeedback.showWarning(
      `${this.uploadResults.success} file${this.uploadResults.success > 1 ? 's' : ''} uploaded successfully, ${this.uploadResults.failed} failed!`,
      { allowWrap: true }
    );
  }

  readonly documentUploadValidationConfig: DocumentValidationConfig;

  constructor(
    private documentUploadService: DocumentUploadService,
    private leadsService: LeadsService
  ) {
    this.documentUploadValidationConfig =
      this.documentUploadService.getCommonFileTypeConfigs().documentsWithImages;
  }

  ngOnInit(): void {
    // Initialize folderHierarchyId from entityRootFolder if available, otherwise use ROOT
    const baseFolder = this.getBaseFolderId();
    this.folderHierarchyId = baseFolder;
    // Initialize with empty stack - we're at the base level
    this.folderNavigationStack = [];
    
    if (this.entityId !== null && this.entityId !== undefined) {
      this.loadDocuments();
    }
  }

  /**
   * Cancel an upload by file ID
   */
  cancelUpload(fileId: string): void {
    // Unsubscribe from the upload subscription (if it still exists)
    const subscription = this.uploadSubscriptions.get(fileId);
    if (subscription) {
      subscription.unsubscribe();
      this.uploadSubscriptions.delete(fileId);
    }

    // Clear the progress interval (if it still exists)
    const interval = this.uploadIntervals.get(fileId);
    if (interval) {
      clearInterval(interval);
      this.uploadIntervals.delete(fileId);
    }

    // Update the UI to show the upload was cancelled (only if subscription existed)
    // If subscription doesn't exist, the upload already completed, so don't update UI
    if (subscription) {
      this.documentUploadComponent?.updateFileProgress(fileId, 0, 'error', 'Upload cancelled');
    }
  }

  ngOnDestroy(): void {
    // Clean up all upload subscriptions
    this.uploadSubscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
    this.uploadSubscriptions.clear();

    // Clean up all progress intervals
    this.uploadIntervals.forEach(interval => {
      clearInterval(interval);
    });
    this.uploadIntervals.clear();
    // Cleanup if needed
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update folderHierarchyId if entityRootFolder changes
    if (changes['entityRootFolder']) {
      const baseFolder = this.getBaseFolderId();
      this.folderHierarchyId = baseFolder;
      // Reset navigation stack - we're at the base level
      this.folderNavigationStack = [];
      // Reload documents if entityId is available and this is not the first change
      if (this.entityId !== null && this.entityId !== undefined && !changes['entityRootFolder'].firstChange) {
        this.loadDocuments();
      }
    }
    
    if (changes['entityId'] && !changes['entityId'].firstChange) {
      if (this.entityId !== null && this.entityId !== undefined) {
        // Reset to base folder when lead changes
        const baseFolder = this.getBaseFolderId();
        this.folderHierarchyId = baseFolder;
        this.folderNavigationStack = [];
        this.loadDocuments();
      }
    }
  }

  loadDocuments(keyword?: string, suppressLoadingState: boolean = false): void {
    if (!this.entityId) {
      this.error = 'No lead ID provided';
      return;
    }

    // Only set loading state if not suppressed (e.g., during post-upload reloads to prevent flashing)
    if (!suppressLoadingState) {
      this.loading = true;
    }
    this.error = null;

    this.leadsService.getLeadDocuments(
      this.entityId,
      this.folderHierarchyId,
      keyword
    ).subscribe({
      next: (response: DocumentListResponse) => {
        this.loading = false;
        if (response.success && response.data) {
          // The new API response has data.rows array
          const responseData = response.data as any;
          let allRows = responseData?.rows || (Array.isArray(responseData) ? responseData : []);
          
          // Store original rows before slicing for subfolder detection
          const originalRows = [...allRows];
          
          // The first item (data.rows[0]) always represents the current folder and should be skipped
          // Extract the current folder's node ID from the first item before slicing (only when not at base folder level)
          const baseFolder = this.getBaseFolderId();
          if (this.folderHierarchyId !== baseFolder && allRows.length > 0) {
            const firstItem = allRows[0];
            // Store the folder node ID for uploads
            this.currentFolderNodeId = firstItem?.id ? Number(firstItem.id) : null;
          } else {
            // At base folder level, no folder node ID
            this.currentFolderNodeId = null;
          }
          
          // Always skip the first item as it represents the current folder itself
          if (allRows.length > 0) {
            allRows = allRows.slice(1);
          }
          
          // Transform the API response to match our ExtendedDocumentFile interface
          const newDocuments = this.documentUploadService.transformApiDocuments(allRows);
          
          // If we just uploaded files and are at base folder, check if we need to navigate to a subfolder
          if (this.justUploadedFiles && this.folderHierarchyId === baseFolder && newDocuments.length === 0) {
            // Check if the ROOT folder item (first item before slicing) indicates files are in a subfolder
            if (originalRows.length > 0) {
              const rootFolderItem = originalRows[0];
              // If ROOT has itemCount > 0 but we got no documents, files are in a subfolder
              if (rootFolderItem.itemCount > 0) {
                let targetPath: string | null = null;
                
                // First, check if there are child items in the rows
                if (originalRows.length > 1) {
                  const firstChild = originalRows[1];
                  if (firstChild && firstChild.folderPath) {
                    targetPath = firstChild.folderPath;
                  }
                }
                
                // If no child items, check if ROOT folder itself has a folderPath pointing to subfolder
                if (!targetPath && rootFolderItem.folderPath && rootFolderItem.folderPath !== baseFolder) {
                  targetPath = rootFolderItem.folderPath;
                }
                
                if (targetPath) {
                  // Navigate to the folder where the file was uploaded
                  const firstSegment = this.extractFirstPathSegment(targetPath);
                  this.folderHierarchyId = firstSegment;
                  // Retry loading with the new path
                  setTimeout(() => {
                    this.loadDocuments(keyword, true);
                  }, 100);
                  return;
                }
              }
            }
          }
          
          // If we just uploaded files and got empty results, retry with exponential backoff
          // Don't update documents array yet - keep existing documents visible to prevent flash
          if (this.justUploadedFiles && newDocuments.length === 0 && this.retryCount < this.maxRetries) {
            this.retryCount++;
            const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 3000); // Exponential backoff, max 3 seconds
            // Keep loading state false and suppress it on retry to prevent flashing
            // Don't update documents array - keep existing documents visible
            setTimeout(() => {
              this.loadDocuments(keyword, true);
            }, retryDelay);
            return; // Don't process empty results yet - keep existing documents visible
          }
          
          // Update documents array only if we got results or exhausted retries
          this.documents = newDocuments;
          
          // If we've exhausted retries or got results, clear the flag
          if (this.justUploadedFiles) {
            this.justUploadedFiles = false;
            this.retryCount = 0;
          }
          
          // Update folder levels if in list view
          if (this.currentViewMode === 'list') {
            const baseFolder = this.getBaseFolderId();
            if (this.folderLevels.length > 0) {
              // Find the level that matches the current folderHierarchyId
              const matchingLevelIndex = this.folderLevels.findIndex(level => level.folderId === this.folderHierarchyId);
              
              if (matchingLevelIndex !== -1) {
                // Update the matching level with new documents
                const matchingLevel = this.folderLevels[matchingLevelIndex];
                matchingLevel.documents = this.documents;
                matchingLevel.folderNodeId = this.currentFolderNodeId;
                matchingLevel.loading = false;
                matchingLevel.error = null;
              } else {
                // If no matching level found, only add if we're at the base folder or if folderLevels is empty
                // This prevents duplicates when switching views
                const isBaseFolder = this.folderHierarchyId === baseFolder;
                if (isBaseFolder || this.folderLevels.length === 0) {
                  // Add new level only if it doesn't already exist
                  const levelExists = this.folderLevels.some(level => level.folderId === this.folderHierarchyId);
                  if (!levelExists) {
                    this.folderLevels.push({
                      folderId: this.folderHierarchyId,
                      folderName: this.folderHierarchyId === baseFolder ? 'Root' : 'Folder',
                      folderPath: this.folderHierarchyId,
                      documents: this.documents,
                      folderNodeId: this.currentFolderNodeId,
                      loading: false,
                      error: null
                    });
                  }
                }
              }
            } else {
              // Initialize folder levels if not already initialized
              this.folderLevels = [{
                folderId: this.folderHierarchyId,
                folderName: this.folderHierarchyId === baseFolder ? 'Root' : 'Folder',
                folderPath: this.folderHierarchyId,
                documents: this.documents,
                folderNodeId: this.currentFolderNodeId,
                loading: false,
                error: null
              }];
            }
          }
          
          // Emit the document count
          this.documentCountChange.emit(this.documents.length);
        } else {
          // If we just uploaded files and got an error, retry
          if (this.justUploadedFiles && this.retryCount < this.maxRetries) {
            this.retryCount++;
            const retryDelay = Math.min(1000 * Math.pow(2, this.retryCount - 1), 3000);
            // Suppress loading state on retry to prevent flashing
            setTimeout(() => {
              this.loadDocuments(keyword, true);
            }, retryDelay);
            return;
          }
          
          // Clear flag if we've exhausted retries
          if (this.justUploadedFiles) {
            this.justUploadedFiles = false;
            this.retryCount = 0;
          }
          
          this.error = response.message || 'Failed to load documents';
          // Emit 0 if no documents
          this.documentCountChange.emit(0);
        }
      },
      error: (error) => {
        this.loading = false;
        this.error = error.message || 'Failed to load documents';
        console.error('Error loading documents:', error);
      }
    });
  }

  onDocumentUploadComplete(files: DocumentFile[]): void {
    // This method is called when upload is complete but we handle the actual upload in onDocumentsSelected
  }

  private uploadFileToApi(fileItem: DocumentFileItem): Observable<void> {
    // Convert DocumentFile to File object for upload
    const fileBlob = this.dataURLToBlob(fileItem.file);
    const fileObject = new File([fileBlob], fileItem.name, { type: fileItem.type });

    // Update progress to uploading
    this.documentUploadComponent?.updateFileProgress(fileItem.id, 0, 'uploading');

    // Simulate incremental upload progress while API call is in progress
    let currentProgress = 0;
    const progressInterval = setInterval(() => {
      // Increment progress by 5-15% each time, but cap at 90% until API responds
      const increment = Math.min(15, Math.max(5, Math.floor(Math.random() * 10) + 5));
      currentProgress = Math.min(90, currentProgress + increment);
      this.documentUploadComponent?.updateFileProgress(fileItem.id, currentProgress, 'uploading');
    }, 500);

    // Store the interval for cancellation
    this.uploadIntervals.set(fileItem.id, progressInterval);

    // Get the active folder node ID based on view mode
    const activeFolderNodeId = this.getActiveFolderNodeId();
    // Store the current folderHierarchyId to check if we're at ROOT/base folder
    const baseFolder = this.getBaseFolderId();
    const isAtBaseFolder = this.folderHierarchyId === baseFolder;
    
    const uploadObservable = this.leadsService.uploadDocument(this.entityId!, fileObject, "DOCUMENT", activeFolderNodeId).pipe(
      map((response: DocumentUploadResponse) => {
        // Clear interval and remove from tracking
        const interval = this.uploadIntervals.get(fileItem.id);
        if (interval) {
          clearInterval(interval);
          this.uploadIntervals.delete(fileItem.id);
        }
        if (response.success) {
          // If we're at ROOT/base folder and the response contains a path, store it for later update
          // We'll update folderHierarchyId after all uploads complete to avoid race conditions
          if (isAtBaseFolder && response.data && !this.firstUploadPath) {
            let responseData: any;
            // Handle both string (JSON) and object responses
            if (typeof response.data === 'string') {
              try {
                responseData = JSON.parse(response.data);
              } catch (e) {
                // If parsing fails, treat as regular string
                responseData = null;
              }
            } else if (typeof response.data === 'object') {
              responseData = response.data;
            }

            // Try multiple possible path properties
            let pathToUse: string | null = null;
            if (responseData) {
              pathToUse = responseData.path || responseData.folderPath || responseData.filePath || null;
            }
            
            // If responseData has a path property, store it for later
            if (pathToUse && typeof pathToUse === 'string') {
              const firstSegment = this.extractFirstPathSegment(pathToUse);
              this.firstUploadPath = firstSegment;
            }
          }
          
          // Update progress to success
          this.documentUploadComponent?.updateFileProgress(fileItem.id, 100, 'success');
          this.uploadResults.success++;
          // Remove subscription from tracking (already removed in individual subscription handler, but keep for safety)
          this.uploadSubscriptions.delete(fileItem.id);
        } else {
          // Match opportunities-documents-tab: surface API message or first errors[] entry for the upload modal row
          const apiMsg =
            primaryApiValidationMessage(response.message, response.errors) ||
            'Failed to upload document';
          this.documentUploadComponent?.updateFileProgress(fileItem.id, 100, 'error', apiMsg);
          this.uploadResults.failed++;
          // Remove subscription from tracking
          this.uploadSubscriptions.delete(fileItem.id);
        }
        return void 0;
      }),
      catchError((error: unknown) => {
        // Clear interval and remove from tracking
        const interval = this.uploadIntervals.get(fileItem.id);
        if (interval) {
          clearInterval(interval);
          this.uploadIntervals.delete(fileItem.id);
        }
        // Remove subscription from tracking
        this.uploadSubscriptions.delete(fileItem.id);
        const uploadErrMsg = extractUploadHttpErrorMessage(error, 'Upload failed');
        this.documentUploadComponent?.updateFileProgress(fileItem.id, 100, 'error', uploadErrMsg);
        this.uploadResults.failed++;
        console.error('Error uploading document:', error);
        // Return an observable that completes without error to continue with other uploads
        return of(void 0);
      })
    );

    return uploadObservable;
  }

  onDocumentsSelected(files: DocumentFileItem[]): void {
    if (!this.entityId) {
      this.showUploadResult('error', 0, 'upload', 'failed - no lead ID');
      return;
    }

    // Store the original folder state before uploads start
    const originalFolderHierarchyId = this.folderHierarchyId;
    const baseFolder = this.getBaseFolderId();
    const wasAtBaseFolder = originalFolderHierarchyId === baseFolder;

    // Reset upload results and first upload path for new batch
    this.uploadResults = { success: 0, failed: 0 };
    this.firstUploadPath = null;

    // Track upload progress for all files
    const uploadObservables: Observable<void>[] = [];

    // For each file, create an observable for the upload and track progress
    files.forEach(fileItem => {
      // Use share() to allow multiple subscriptions without re-executing the HTTP request
      const uploadObservable = this.uploadFileToApi(fileItem).pipe(share());
      uploadObservables.push(uploadObservable);
      
      // Subscribe individually to track the subscription for cancellation
      const subscription = uploadObservable.subscribe({
        next: () => {
          // Remove subscription when complete
          this.uploadSubscriptions.delete(fileItem.id);
        },
        error: () => {
          // Remove subscription on error
          this.uploadSubscriptions.delete(fileItem.id);
        }
      });
      this.uploadSubscriptions.set(fileItem.id, subscription);
    });

    // Wait for all uploads to complete before reloading documents using forkJoin
    forkJoin(uploadObservables).subscribe({
      next: () => {
        // If we were at base folder and got a path from the first upload, update folderHierarchyId now
        if (wasAtBaseFolder && this.firstUploadPath) {
          this.folderHierarchyId = this.firstUploadPath;
        }
        
        // Show final upload result summary
        this.showBatchUploadResult();
        // Reset upload results
        this.uploadResults = { success: 0, failed: 0 };
        // Reset first upload path
        this.firstUploadPath = null;
        
        // Set flag to indicate we just uploaded files - this will trigger retry logic if reload returns empty
        this.justUploadedFiles = true;
        this.retryCount = 0;
        
        // Wait longer to ensure API has fully processed the upload before reloading
        // This prevents the "flash" where it briefly shows content then goes back to empty
        // Also delay to show the success message first
        setTimeout(() => {
          // Reload the active folder based on view mode, suppressing loading state to prevent flash
          this.reloadActiveFolderWithSuppressedLoading();
        }, 1000);
      },
      error: (error) => {
        console.error('Some file uploads failed:', error);
        
        // If we were at base folder and got a path from the first upload, update folderHierarchyId now
        if (wasAtBaseFolder && this.firstUploadPath) {
          this.folderHierarchyId = this.firstUploadPath;
        }
        
        // Show final upload result summary even on error
        this.showBatchUploadResult();
        // Reset upload results
        this.uploadResults = { success: 0, failed: 0 };
        // Reset first upload path
        this.firstUploadPath = null;
        
        // Set flag to indicate we just uploaded files - this will trigger retry logic if reload returns empty
        this.justUploadedFiles = true;
        this.retryCount = 0;
        
        // Wait longer to ensure API has fully processed the upload before reloading
        setTimeout(() => {
          // Still reload documents to show any successful uploads, suppressing loading state
          this.reloadActiveFolderWithSuppressedLoading();
        }, 1000);
      }
    });
  }

  deleteItem(document: ExtendedDocumentFile): void {
    // Check if it's a folder
    const isFolder = document.isFolder === true || document.extension === 'folder';
    
    if (isFolder && document.folderNodeId !== undefined && document.folderNodeId !== null) {
      // Delete folder using folderNodeId
      this.leadsService.deleteFolder(document.folderNodeId).subscribe({
        next: (response: DocumentDeleteResponse) => {
          if (response.success) {
            this.showUploadResult('deleted', 1, 'folder', 'deleted', false);
            // Remove from local array
            this.documents = this.documents.filter(doc => doc.id !== document.id);
            // Also remove from folder levels if in list view
            if (this.folderLevels) {
              this.folderLevels.forEach(level => {
                level.documents = level.documents.filter(doc => doc.id !== document.id);
              });
            }
            // Emit the updated document count
            this.documentCountChange.emit(this.documents.length);
            // Reload documents to refresh the view
            this.reloadActiveFolder();
          } else {
            this.showUploadResult('failed', 1, 'folder', 'failed to delete', false);
          }
        },
        error: (error) => {
          this.showUploadResult('failed', 1, 'folder', 'failed to delete', false);
          console.error('Error deleting folder:', error);
        }
      });
    } else {
      // Delete document using folderItemId
      if (document.folderItemId === undefined || document.folderItemId === null) {
        console.error('Cannot delete document: folderItemId is required', document);
        this.showUploadResult('failed', 1, 'file', 'failed to delete - missing folderItemId', false);
        return;
      }
      this.deleteDocument(document.folderItemId, document.id);
    }
  }

  deleteDocument(folderItemId: number, documentId: string): void {
    this.leadsService.deleteDocument(folderItemId).subscribe({
      next: (response: DocumentDeleteResponse) => {
        if (response.success) {
          this.showUploadResult('deleted', 1, 'file', 'deleted', false);
          // Remove from local array
          this.documents = this.documents.filter(doc => doc.id !== documentId);
          // Also remove from folder levels if in list view
          if (this.folderLevels) {
            this.folderLevels.forEach(level => {
              level.documents = level.documents.filter(doc => doc.id !== documentId);
            });
          }
          // Emit the updated document count
          this.documentCountChange.emit(this.documents.length);
          // Reload documents to refresh the view
          this.reloadActiveFolder();
        } else {
          this.showUploadResult('failed', 1, 'file', 'failed to delete', false);
        }
      },
      error: (error) => {
        this.showUploadResult('failed', 1, 'file', 'failed to delete', false);
        console.error('Error deleting document:', error);
      }
    });
  }

  downloadDocument(document: ExtendedDocumentFile): void {
    const directUrl = pickAttachmentDisplayUrlFromDocument(document);
    if (directUrl) {
      this.downloadFileFromUrl(directUrl, document.name);
      this.showUploadResult('downloaded', 1, 'file', 'downloaded successfully');
      return;
    }

    // Use entityId for attachmentId, fallback to id if entityId is not available
    const attachmentId = document.entityId || document.id;
    this.leadsService.downloadDocument(attachmentId).subscribe({
      next: (response: DocumentDownloadResponse) => {
        if (response.success && response.data) {
          // Handle the actual API response structure
          if (typeof response.data === 'object' && response.data !== null) {
            const dataObj = response.data as Record<string, unknown>;

            const downloadUrl = pickAttachmentDisplayUrlFromApiData(dataObj);
            if (downloadUrl) {
              this.downloadFileFromUrl(downloadUrl, document.name);
              this.showUploadResult('downloaded', 1, 'file', 'downloaded successfully');
              return;
            }

            // Fallback: try to extract base64 from common properties
            const base64Data =
              (typeof dataObj['base64'] === 'string' ? dataObj['base64'] : '') ||
              (typeof dataObj['data'] === 'string' ? dataObj['data'] : '') ||
              (typeof dataObj['content'] === 'string' ? dataObj['content'] : '') ||
              (typeof dataObj['file'] === 'string' ? dataObj['file'] : '');

            if (base64Data) {
              this.downloadFileFromBase64(base64Data, document.name);
              this.showUploadResult('downloaded', 1, 'file', 'downloaded successfully');
            } else {
              console.error('❌ No download URL or base64 data found in response object');
              this.showUploadResult('failed', 1, 'file', 'failed to download - no data');
            }
          } else if (typeof response.data === 'string') {
            // Handle string data (base64)
            this.downloadFileFromBase64(response.data, document.name);
            this.showUploadResult('downloaded', 1, 'file', 'downloaded successfully');
          } else {
            console.error('❌ Unexpected data type:', typeof response.data);
            this.showUploadResult('failed', 1, 'file', 'failed to download - invalid format');
          }
        } else {
          this.showUploadResult('failed', 1, 'file', 'failed to download');
        }
      },
      error: (error) => {
        this.showUploadResult('failed', 1, 'file', 'failed to download');
        console.error('Error downloading document:', error);
      }
    });
  }

  /**
   * Download file from base64 data
   */
  private downloadFileFromBase64(base64Data: string, fileName: string): void {
    try {
      // Validate base64 data
      if (!base64Data || typeof base64Data !== 'string') {
        throw new Error('Invalid base64 data provided');
      }

      // Remove data URL prefix if present
      const base64 = base64Data.replace(/^data:[^;]+;base64,/, '');

      // Validate base64 format
      if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
        throw new Error('Invalid base64 format');
      }

      // Convert base64 to blob
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/octet-stream' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('❌ Error creating download:', error);
      this.showUploadResult('failed', 1, 'file', 'failed to create download');
    }
  }

  /**
   * Download file from direct URL
   */
  private downloadFileFromUrl(fileUrl: string, fileName: string): void {
    try {
      // Create download link
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.target = '_blank'; // Open in new tab if download fails

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
    } catch (error) {
      console.error('❌ Error creating download from URL:', error);
      this.showUploadResult('failed', 1, 'file', 'failed to create download');
    }
  }


  /**
   * Convert File to base64 string for upload
   */
  private dataURLToBlob(file: File): Blob {
    // Since we're working with a File object directly, we can return it as a Blob
    return file;
  }

  onDocumentPreview(document: ExtendedDocumentFile): void {
    this.previewDocument = document;
    this.showPreviewModal = true;
  }

  onPreviewClose(isOpen: boolean): void {
    this.showPreviewModal = isOpen;
    if (!isOpen) {
      this.previewDocument = null;
    }
  }

  onPreviewDownload(document: ExtendedDocumentFile): void {
    this.downloadDocument(document);
  }

  /**
   * Open the upload modal (called from parent component)
   */
  public openUploadModal(): void {
    if (this.documentUploadComponent) {
      this.documentUploadComponent.openUploadModal();
    }
  }

  /**
   * Check if we're in zero state (no documents)
   */
  public isInZeroState(): boolean {
    return !this.loading && !this.error && (!this.documents || this.documents.length === 0);
  }

  /**
   * Handle folder creation request from child component
   */
  onFolderCreateRequest(request: CreateFolderRequest): void {
    if (this.folderCreateInProgress) {
      return;
    }
    this.folderCreateInProgress = true;
    this.folderCreationSuccess = false;
    this.folderCreationError = '';

    this.leadsService.createFolder(request).subscribe({
      next: (response) => {
        this.folderCreateInProgress = false;
        this.folderCreationSuccess = true;
        this.onFolderCreated(response);
        // Reset after child has had time to see the success and close the modal (avoids spinner stuck on)
        setTimeout(() => {
          this.folderCreationSuccess = false;
        }, 150);
      },
      error: (error) => {
        this.folderCreateInProgress = false;
        console.error('❌ Error creating folder:', error);
        this.onFolderCreationError('error');
      }
    });
  }

  // Rename folder state
  folderRenameSuccess = false;
  folderRenameError: string = '';

  // Create folder state
  folderCreationSuccess = false;
  folderCreationError: string = '';
  private folderCreateInProgress = false;

  /**
   * Handle clear rename error (when user starts typing)
   */
  onRenameFolderClearError(): void {
    this.folderRenameError = '';
  }

  /**
   * Handle clear create folder error (when user starts typing)
   */
  onFolderCreationClearError(): void {
    this.folderCreationError = '';
  }

  /**
   * Handle folder rename request
   */
  onFolderRenameRequest(request: { folderNodeId: number; name: string }): void {
    // Clear previous state
    this.folderRenameSuccess = false;
    this.folderRenameError = '';

    this.leadsService.renameFolder(request.folderNodeId, request.name).subscribe({
      next: (response: GenericApiResponse) => {
        if (response.success) {
          this.showUploadResult('renamed', 1, 'folder', 'renamed successfully');
          // Update the folder name in local arrays
          this.updateFolderName(request.folderNodeId, request.name);
          // Reload documents to refresh the view
          this.reloadActiveFolder();
          // Signal success to child component
          this.folderRenameSuccess = true;
          // Reset after change detection has time to process
          setTimeout(() => {
            this.folderRenameSuccess = false;
          }, 100);
        } else {
          // Set a flag to show error (the modal will format the message with the folder name)
          this.folderRenameError = 'error';
        }
      },
      error: (error) => {
        console.error('❌ Error renaming folder:', error);
        // Set a flag to show error (the modal will format the message with the folder name)
        this.folderRenameError = 'error';
      }
    });
  }

  /**
   * Update folder name in local arrays
   */
  private updateFolderName(folderNodeId: number, newName: string): void {
    // Update in documents array
    const folder = this.documents.find(doc => doc.folderNodeId === folderNodeId);
    if (folder) {
      folder.name = newName;
    }

    // Update in folder levels (for list view)
    if (this.folderLevels) {
      this.folderLevels.forEach(level => {
        const folderInLevel = level.documents.find(doc => doc.folderNodeId === folderNodeId);
        if (folderInLevel) {
          folderInLevel.name = newName;
          // Also update the folder name in the level if it matches
          if (level.folderNodeId === folderNodeId) {
            level.folderName = newName;
          }
        }
      });
    }

    // Update in folder navigation stack
    this.folderNavigationStack.forEach(stackItem => {
      if (stackItem.folderNodeId === folderNodeId) {
        stackItem.folderName = newName;
      }
      const folderInStack = stackItem.documents.find(doc => doc.folderNodeId === folderNodeId);
      if (folderInStack) {
        folderInStack.name = newName;
      }
    });
  }

  onFolderCreated(response: CreateFolderResponse | string): void {
    // Handle both old format (string) and new format (CreateFolderResponse) for backward compatibility
    const folderName = typeof response === 'string' ? response : 'folder';
    
    this.showUploadResult('created', 1, 'folder', 'created successfully');
    
    // If we're at ROOT/base folder and the response contains a path, update folderHierarchyId
    if (typeof response === 'object' && response.success && response.data) {
      const baseFolder = this.getBaseFolderId();
      const isAtBaseFolder = this.folderHierarchyId === baseFolder;
      
      if (isAtBaseFolder) {
        let responseData: any;
        // Handle both string (JSON) and object responses
        if (typeof response.data === 'string') {
          try {
            responseData = JSON.parse(response.data);
          } catch (e) {
            // If parsing fails, treat as regular string
            responseData = null;
          }
        } else if (typeof response.data === 'object' && response.data !== null) {
          responseData = response.data;
        }
        
        // If responseData has a path property, update folderHierarchyId
        if (responseData && responseData.path && typeof responseData.path === 'string') {
          const firstSegment = this.extractFirstPathSegment(responseData.path);
          this.folderHierarchyId = firstSegment;
        }
      }
    }
    
    // Reload the active folder to show the new folder
    this.reloadActiveFolder();
  }

  /**
   * Handle folder creation error
   */
  onFolderCreationError(errorMessage: string): void {
    // Set the error to display in the modal
    this.folderCreationError = errorMessage;
  }

  /**
   * Handle folder click - navigate into folder
   * In list view, opens folder to the right; in gallery view, replaces current view
   */
  onFolderClick(folder: ExtendedDocumentFile): void {
    if (!folder.folderPath) {
      console.error('Folder path is required to navigate');
      return;
    }
    
    // Check current view mode
    if (this.currentViewMode === 'list') {
      // In list view: add new folder level to the right
      this.loadFolderLevel(folder);
    } else {
      // In gallery view: store current folder info in navigation stack before navigating
      const baseFolder = this.getBaseFolderId();
      const currentFolderName = this.folderHierarchyId === baseFolder 
        ? 'Root' 
        : this.getFolderNameFromCurrentPath();
      
      this.folderNavigationStack.push({
        folderId: this.folderHierarchyId,
        folderName: currentFolderName,
        folderPath: this.folderHierarchyId,
        folderNodeId: this.currentFolderNodeId,
        documents: [...this.documents] // Store a copy of current documents
      });
      this.folderHierarchyId = folder.folderPath;
      this.loadDocuments();
    }
  }

  /**
   * Handle back button click - navigate back to previous folder
   * Never navigates to ROOT if entityRootFolder exists - only goes to entityRootFolder
   */
  onBackClick(): void {
    // Don't allow going back if we're already at the base folder
    if (this.isAtBaseFolder()) {
      return;
    }

    // Check current view mode
    if (this.currentViewMode === 'list') {
      // In list view: remove the last folder level
      if (this.folderLevels.length > 0) {
        this.folderLevels.pop();
        // Update main documents to show the previous level
        if (this.folderLevels.length > 0) {
          const previousLevel = this.folderLevels[this.folderLevels.length - 1];
          this.documents = previousLevel.documents;
          this.folderHierarchyId = previousLevel.folderId;
          this.currentFolderNodeId = previousLevel.folderNodeId;
          
          // Sync navigation stack - remove the last item if it matches the current folder
          if (this.folderNavigationStack.length > 0) {
            const lastStackItem = this.folderNavigationStack[this.folderNavigationStack.length - 1];
            if (lastStackItem.folderId === this.folderHierarchyId) {
              // Already in sync, no need to pop
            } else {
              // Pop the last item to keep in sync
              this.folderNavigationStack.pop();
            }
          }
        } else {
          // Back to base folder
          const baseFolder = this.getBaseFolderId();
          this.folderHierarchyId = baseFolder;
          this.currentFolderNodeId = null;
          this.folderNavigationStack = []; // Clear navigation stack when back to base
          this.loadDocuments();
        }
      }
    } else {
      // In gallery view: restore from navigation stack
      if (this.folderNavigationStack.length > 0) {
        const previousFolder = this.folderNavigationStack.pop();
        if (previousFolder) {
          this.folderHierarchyId = previousFolder.folderId;
          this.currentFolderNodeId = previousFolder.folderNodeId;
          this.documents = previousFolder.documents;
          // Don't reload documents, use the stored ones
        }
      } else {
        const baseFolder = this.getBaseFolderId();
        if (this.folderHierarchyId !== baseFolder) {
          this.folderHierarchyId = baseFolder;
          this.currentFolderNodeId = null;
          this.loadDocuments();
        }
      }
    }
  }

  /**
   * Load documents for a specific folder level (used in list view)
   */
  private loadFolderLevel(folder: ExtendedDocumentFile): void {
    if (!this.entityId || !folder.folderPath) {
      return;
    }

    // Find which column this folder belongs to
    const folderColumnIndex = this.findFolderColumnIndex(folder);
    
    // If folder is found in a column, remove all columns after it (close any open folders from that column or subsequent columns)
    if (folderColumnIndex !== -1) {
      // Remove all columns after the folder's column
      this.folderLevels = this.folderLevels.slice(0, folderColumnIndex + 1);
    }

    // Check if the folder level already exists to prevent duplicates
    const levelExists = this.folderLevels.some(level => level.folderId === folder.folderPath);
    if (levelExists) {
      // Folder level already exists, don't add duplicate
      return;
    }

    // Store current folder in navigation stack for gallery view sync
    const baseFolder = this.getBaseFolderId();
    const currentFolderName = this.folderHierarchyId === baseFolder 
      ? 'Root' 
      : this.getFolderNameFromCurrentPath();
    
    // Only add to navigation stack if not already there (to avoid duplicates)
    const existingIndex = this.folderNavigationStack.findIndex(f => f.folderId === this.folderHierarchyId);
    if (existingIndex === -1) {
      this.folderNavigationStack.push({
        folderId: this.folderHierarchyId,
        folderName: currentFolderName,
        folderPath: this.folderHierarchyId,
        folderNodeId: this.currentFolderNodeId,
        documents: [...this.documents]
      });
    }

    // Add a new folder level with loading state
    const newLevel = {
      folderId: folder.folderPath,
      folderName: folder.name,
      folderPath: folder.folderPath,
      documents: [],
      folderNodeId: null,
      loading: true,
      error: null
    };
    this.folderLevels.push(newLevel);

    // Load documents for this folder
    this.leadsService.getLeadDocuments(
      this.entityId,
      folder.folderPath
    ).subscribe({
      next: (response: DocumentListResponse) => {
        const levelIndex = this.folderLevels.length - 1;
        if (levelIndex >= 0) {
          const level = this.folderLevels[levelIndex];
          level.loading = false;
          
          if (response.success && response.data) {
            const responseData = response.data as any;
            let rows = responseData?.rows || (Array.isArray(responseData) ? responseData : []);
            
            // Extract folder node ID from first item
            if (rows.length > 0) {
              const firstItem = rows[0];
              level.folderNodeId = firstItem?.id ? Number(firstItem.id) : null;
              rows = rows.slice(1);
            }
            
            // Transform and store documents
            level.documents = this.documentUploadService.transformApiDocuments(rows);
            
            // Scroll to the rightmost column after the new folder is loaded
            if (this.documentUploadComponent) {
              this.documentUploadComponent.scrollToRightmostColumn();
            }
          } else {
            level.error = response.message || 'Failed to load documents';
          }
        }
      },
      error: (error) => {
        const levelIndex = this.folderLevels.length - 1;
        if (levelIndex >= 0) {
          const level = this.folderLevels[levelIndex];
          level.loading = false;
          level.error = error.message || 'Failed to load documents';
        }
        console.error('Error loading folder level:', error);
      }
    });
  }

  /**
   * Handle view mode change from document-upload component
   */
  onViewModeChange(viewMode: 'gallery' | 'list'): void {
    this.currentViewMode = viewMode;
    
    // When switching to gallery view, reset folder levels and use main documents
    if (viewMode === 'gallery') {
      this.folderLevels = [];
      // Ensure main documents are loaded
      if (this.documents.length === 0) {
        this.loadDocuments();
      }
    } else {
      // When switching to list view, build folder levels from navigation stack + current folder
      if (this.folderLevels.length === 0) {
        this.buildFolderLevelsFromNavigation();
      }
    }
  }

  /**
   * Build folder levels from navigation stack and current folder
   * This is called when switching to list view to show the full folder hierarchy
   */
  private buildFolderLevelsFromNavigation(): void {
    const baseFolder = this.getBaseFolderId();
    this.folderLevels = [];

    // Add all folders from navigation stack (previous folders)
    // Use a Set to track folder IDs to prevent duplicates
    const addedFolderIds = new Set<string>();
    
    for (const folderInfo of this.folderNavigationStack) {
      // Only add if not already added (prevent duplicates)
      if (!addedFolderIds.has(folderInfo.folderId)) {
      this.folderLevels.push({
        folderId: folderInfo.folderId,
        folderName: folderInfo.folderName,
        folderPath: folderInfo.folderPath,
        documents: folderInfo.documents,
        folderNodeId: folderInfo.folderNodeId,
        loading: false,
        error: null
      });
        addedFolderIds.add(folderInfo.folderId);
      }
    }

    // Add current folder only if not already added
    if (!addedFolderIds.has(this.folderHierarchyId)) {
    const currentFolderName = this.folderHierarchyId === baseFolder 
      ? 'Root' 
      : this.getFolderNameFromDocuments(this.folderHierarchyId);
    
    this.folderLevels.push({
      folderId: this.folderHierarchyId,
      folderName: currentFolderName,
      folderPath: this.folderHierarchyId,
      documents: this.documents.length > 0 ? this.documents : [],
      folderNodeId: this.currentFolderNodeId,
      loading: this.loading,
      error: this.error
    });
      addedFolderIds.add(this.folderHierarchyId);
    }

    // If current folder has no documents and is not loading, try to load them
    if (this.documents.length === 0 && !this.loading && !this.error) {
      this.loadDocuments();
    }
  }

  /**
   * Get folder name from current path
   * Used to get the name of the current folder
   */
  private getFolderNameFromCurrentPath(): string {
    // Try to find the folder in current documents by matching folderPath
    const folder = this.documents.find(doc => doc.isFolder && doc.folderPath === this.folderHierarchyId);
    if (folder) {
      return folder.name;
    }
    // Fallback: extract name from path or return 'Folder'
    const pathParts = this.folderHierarchyId.split('/');
    return pathParts[pathParts.length - 1] || 'Folder';
  }

  /**
   * Find which column index contains the given folder
   * Returns -1 if folder is not found in any column
   */
  private findFolderColumnIndex(folder: ExtendedDocumentFile): number {
    if (!this.folderLevels || this.folderLevels.length === 0) {
      return -1;
    }

    // Search through each column to find the folder
    for (let i = 0; i < this.folderLevels.length; i++) {
      const level = this.folderLevels[i];
      // Check if the folder exists in this column's documents
      const folderInColumn = level.documents.find(
        doc => doc.isFolder && doc.folderPath === folder.folderPath && doc.id === folder.id
      );
      if (folderInColumn) {
        return i;
      }
    }

    return -1;
  }

  /**
   * Get folder name from documents by matching folderPath
   * Used to get the name of a folder when we only have its path
   */
  private getFolderNameFromDocuments(folderPath: string): string {
    // Try to find the folder in current documents
    const folder = this.documents.find(doc => doc.isFolder && doc.folderPath === folderPath);
    if (folder) {
      return folder.name;
    }
    // Fallback: extract name from path or return 'Folder'
    const pathParts = folderPath.split('/');
    return pathParts[pathParts.length - 1] || 'Folder';
  }

  /**
   * Handle move request from document-upload component
   * Makes the API call and handles success/error
   */
  onMoveRequest(event: { 
    draggedFolderNodeId: number; 
    targetFolderNodeId: number; 
    isMovingFolder: boolean; 
    draggedDocument: ExtendedDocumentFile; 
    targetFolder: ExtendedDocumentFile 
  }): void {
    // Call the appropriate API method
    const moveObservable = event.isMovingFolder
      ? this.leadsService.moveFolder(event.draggedFolderNodeId, event.targetFolderNodeId)
      : this.leadsService.moveItem(event.draggedFolderNodeId, event.targetFolderNodeId);

    moveObservable.subscribe({
      next: (response) => {
        if (response.success) {
          // Emit event to trigger reload with target and source folder info
          this.onItemMoved({
            targetFolder: event.targetFolder,
            sourceFolder: event.draggedDocument
          });
        } else {
          console.error('Failed to move item:', response.message);
        }
      },
      error: (error) => {
        console.error('Error moving item:', error);
      }
    });
  }

  /**
   * Handle item moved event from drag and drop
   */
  onItemMoved(event?: { targetFolder: ExtendedDocumentFile | null; sourceFolder: ExtendedDocumentFile | null }): void {
    this.showUploadResult('moved', 1, 'item', 'moved successfully');
    
    // In list view, reload the specific folder that received the item
    if (this.currentViewMode === 'list' && event?.targetFolder) {
      this.reloadFolderAfterMove(event.targetFolder, event.sourceFolder);
    } else {
      // In gallery view or if no target folder info, reload the active folder
      this.reloadActiveFolder();
    }
  }

  /**
   * Reload folder levels after a drag and drop move in list view
   */
  private reloadFolderAfterMove(targetFolder: ExtendedDocumentFile, sourceFolder: ExtendedDocumentFile | null): void {
    if (!this.entityId || !this.folderLevels || this.folderLevels.length === 0) {
      // Fallback to reloading active folder
      this.reloadActiveFolder();
      return;
    }

    // Find which folder level contains the target folder (the folder that received the item)
    let targetLevelIndex = -1;
    for (let i = 0; i < this.folderLevels.length; i++) {
      const level = this.folderLevels[i];
      const folderInLevel = level.documents.find(doc => 
        doc.isFolder && doc.folderPath === targetFolder.folderPath && doc.id === targetFolder.id
      );
      if (folderInLevel) {
        targetLevelIndex = i;
        break;
      }
    }

    // Reload the target folder level to update its item count
    if (targetLevelIndex !== -1) {
      const targetLevel = this.folderLevels[targetLevelIndex];
      this.reloadFolderLevel(targetLevel);
    }

    // Find which folder level contains the source item (the item that was dragged)
    if (sourceFolder) {
      let sourceLevelIndex = -1;
      for (let i = 0; i < this.folderLevels.length; i++) {
        const level = this.folderLevels[i];
        // Check if the dragged item exists in this level
        const itemInLevel = level.documents.find(doc => doc.id === sourceFolder.id);
        if (itemInLevel) {
          sourceLevelIndex = i;
          break;
        }
      }

      // Reload the source folder level to remove the moved item
      if (sourceLevelIndex !== -1 && sourceLevelIndex !== targetLevelIndex) {
        const sourceLevel = this.folderLevels[sourceLevelIndex];
        this.reloadFolderLevel(sourceLevel);
      }
    }

    // If target folder is opened in a column (rightmost or any column), reload that column
    // Check if target folder's path matches any column's folderId
    for (let i = 0; i < this.folderLevels.length; i++) {
      const level = this.folderLevels[i];
      if (level.folderId === targetFolder.folderPath) {
        // This column shows the contents of the target folder, reload it
        this.reloadFolderLevel(level);
        break;
      }
    }

    // Always reload the rightmost folder level to ensure it's up to date
    if (this.folderLevels.length > 0) {
      const rightmostLevel = this.folderLevels[this.folderLevels.length - 1];
      this.reloadFolderLevel(rightmostLevel);
    }

    // Update parent folder item counts
    this.updateParentFolderItemCount();
  }
}
